import { ControlledAirportAutocomplete } from "v2/modules/form/ControlledAirportsAutocomplete";
import { Button, Flex, IconButton, Text } from "@chakra-ui/react";
import { ArrayPath, FieldValues, Path, UseControllerProps, useFieldArray } from "react-hook-form";
import { Delete } from "@airhelp/icons";
import { useTranslation } from "react-i18next";
import { isRtl } from "utils/getLocaleDirection";
import { useAppSettingsSnapshot } from "v2/settings";

const MAX_CONNECTIONS = 6;

export function ConnectionsControl<T extends FieldValues>(props: UseControllerProps<T>) {
  const { t } = useTranslation();
  const { locale } = useAppSettingsSnapshot();

  const {
    fields: connections,
    append,
    remove,
  } = useFieldArray({
    name: props.name as ArrayPath<T>,
    control: props.control,
  });

  return (
    <>
      {!connections.length && (
        <Flex data-testid="connectingAirport" mb={1}>
          <ControlledAirportAutocomplete
            control={props.control}
            data-testid="connectingAirportAutocomplete"
            name={`connections.0.airport` as Path<T>}
            label={t("steps.connections.connection_label", { index: 1 })}
            placeholder={t("steps.connections.connection_placeholder")}
            formControlStyles={{
              mt: 2,
              w: { base: "100%", md: "50%" },
            }}
            isRtl={isRtl(locale)}
          />
        </Flex>
      )}

      {connections.map((connection, index: number) => (
        <Flex data-testid="connectingAirport" key={connection.id} mb={1}>
          <ControlledAirportAutocomplete
            control={props.control}
            dataTestId="connectingAirportAutocomplete"
            name={`connections.${index}.airport` as Path<T>}
            label={t("steps.connections.connection_label", { index: index + 1 })}
            placeholder={t("steps.connections.connection_placeholder")}
            formControlStyles={{
              mt: 2,
              w: { base: connections.length > 1 ? "calc(100% - 36px)" : "100%", md: "50%" },
            }}
            isRtl={isRtl(locale)}
          />
          {connections.length > 1 && (
            <IconButton
              bg="none"
              border="none"
              height="56px"
              display="flex"
              minWidth="auto"
              alignSelf="center"
              alignItems={"flex-end"}
              marginLeft={3}
              _hover={{ bg: "none", cursor: "pointer" }}
              _focus={{ bg: "none" }}
              _active={{ bg: "none" }}
              aria-label="Delete connecting airport"
              icon={<Delete boxSize={5} color="primary.900" _hover={{ color: "primary.500" }} />}
              onClick={() => remove(index)}
            />
          )}
        </Flex>
      ))}
      {connections.length < MAX_CONNECTIONS && (
        <Button
          border="none"
          bg="transparent"
          cursor="pointer"
          p="unset"
          name="Add connection"
          _hover={{ opacity: 0.8 }}
          _active={{ bg: "none" }}
          _focus={{ opacity: 0.8 }}
          onClick={() => append({ airport: null } as any)}
        >
          <Text variant="medium" color="primary.500">
            {t("steps.connections.add_connection")}
          </Text>
        </Button>
      )}
    </>
  );
}
